import { Injectable, signal } from '@angular/core';

export interface LoaderState {
  show: boolean;
  message?: string;
  overlay?: boolean;
  progress?: number;
  type?: 'modal' | 'spinner' | 'none';
}

@Injectable({
  providedIn: 'root'
})
export class LoaderService {
  private loaderState = signal<LoaderState>({
    show: false,
    message: 'Please wait...',
    overlay: true,
    progress: undefined,
    type: 'modal'
  });

  readonly state = this.loaderState.asReadonly();

  show(config?: Partial<Omit<LoaderState, 'show'>>) {
    this.loaderState.update(state => ({
      ...state,
      show: true,
      message: config?.message ?? 'Please wait...',
      overlay: config?.overlay ?? true,
      progress: config?.progress,
      type: config?.type ?? state.type
    }));
  }

  hide() {
    this.loaderState.update(state => ({
      ...state,
      show: false
    }));
  }

  setMessage(message: string) {
    this.loaderState.update(state => ({
      ...state,
      message
    }));
  }

  setProgress(progress: number) {
    this.loaderState.update(state => ({
      ...state,
      progress: Math.min(100, Math.max(0, progress))
    }));
  }

  isShowing(): boolean {
    return this.loaderState().show;
  }

  async wrap<T>(
    promise: Promise<T>,
    config?: Partial<Omit<LoaderState, 'show'>>
  ): Promise<T> {
    this.show(config);
    try {
      return await promise;
    } finally {
      this.hide();
    }
  }
}
